import type { IconName } from '../components/chrome/Icon';
import type { MediaRow } from './dto';

/**
 * Completed activities the parent did with the baby. The real feature (logging
 * play / song / story / outing sessions) lands later; until then the Journey
 * path shows these deterministic placeholders so the timeline reads as a lived
 * story, not just a list of milestones.
 */
export type ActivityKind = 'play' | 'sing' | 'read' | 'go_out';

export interface PlaceholderActivity {
  id: string;
  kind: ActivityKind;
  title: string;
  /** Weeks-from-birth the activity happened. */
  week: number;
  activityId?: number;
  done_media?: MediaRow[];
}

export const ACTIVITY_META: Record<
  ActivityKind,
  { icon: IconName; label: string; color: string }
> = {
  play:   { icon: 'play',    label: 'Play',   color: '#D98E5A' },
  sing:   { icon: 'sing',    label: 'Sing',   color: '#A579B0' },
  read:   { icon: 'read',    label: 'Read',   color: '#6F9E6E' },
  go_out: { icon: 'explore', label: 'Go out', color: '#5E92B0' },
};

const ORDER: ActivityKind[] = ['play', 'sing', 'read', 'go_out'];

const POOL: Record<ActivityKind, string[]> = {
  play:   ['Tummy-time play', 'Peekaboo giggles', 'Stacking soft blocks', 'Rattle shake'],
  sing:   ['Lullaby before nap', 'Nursery rhymes', 'Clapping song', 'Humming together'],
  read:   ['Bedtime picture book', 'Touch-and-feel book', 'Story on the rug', 'Black-and-white cards'],
  go_out: ['First park visit', 'Stroller walk', "Trip to grandma's", 'Morning in the garden'],
};

/**
 * Deterministic placeholder activities — roughly one every three weeks from
 * week 2 up to the baby's current age, cycling through the four kinds. Stable
 * across renders (no RNG) so the path doesn't reshuffle on every refresh.
 */
export function placeholderActivities(ageWeeks: number): PlaceholderActivity[] {
  const out: PlaceholderActivity[] = [];
  let i = 0;
  for (let w = 2; w <= ageWeeks; w += 3) {
    const kind = ORDER[i % ORDER.length]!;
    const titles = POOL[kind];
    out.push({ id: `act-${w}`, kind, title: titles[i % titles.length]!, week: w });
    i++;
  }
  return out;
}
